import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ShoppingCart, Receipt, BarChart3, CreditCard, Printer, Tag, 
  Monitor, Check, ChevronRight, Play, ArrowRight, Star, Users,
  Zap, Shield, Clock, TrendingUp, Camera, FileText, DollarSign,
  Plus, Minus, Menu, X, Sparkles, CheckCircle2, Store, Utensils, Gift, Truck, QrCode
} from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();
  const [selectedModules, setSelectedModules] = useState({
    pdv: { selected: true, quantity: 1, price: 100, additionalPrice: 50 },
    fiscal: { selected: true, quantity: 1, price: 100 },
    gestao: { selected: true, quantity: 1, price: 100 },
    webtef: { selected: false, quantity: 1, price: 100, additionalPrice: 50 },
    impressao: { selected: true, quantity: 1, price: 0 },
    etiquetas: { selected: false, quantity: 1, price: 300 },
    totem: { selected: false, quantity: 1, price: 200 },
    table_menu: { selected: false, quantity: 1, price: 50 },
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleModule = (module) => {
    setSelectedModules(prev => ({
      ...prev,
      [module]: { ...prev[module], selected: !prev[module].selected }
    }));
  };

  const updateQuantity = (module, delta) => {
    setSelectedModules(prev => ({
      ...prev,
      [module]: { 
        ...prev[module], 
        quantity: Math.max(1, prev[module].quantity + delta)
      }
    }));
  };

  const calculateTotal = () => {
    let total = 0;
    Object.entries(selectedModules).forEach(([key, module]) => {
      if (module.selected) {
        total += module.price;
        if (module.additionalPrice && module.quantity > 1) {
          total += module.additionalPrice * (module.quantity - 1);
        }
        if (key === 'totem') {
          total += module.price * (module.quantity - 1);
        }
      }
    });
    return total;
  };

  const modules = [
    {
      id: 'pdv',
      icon: ShoppingCart,
      title: 'Sistema PDV',
      description: 'Venda rápida, intuitiva e estável. Ideal para balcão, caixa e delivery.',
      price: 'R$ 100,00',
      additionalInfo: '+ R$ 50,00 por PDV adicional',
      color: 'from-emerald-500 to-teal-600',
      features: ['Venda rápida e intuitiva', 'Controle de caixa', 'Múltiplas formas de pagamento', 'Integração com balança']
    },
    {
      id: 'fiscal',
      icon: Receipt,
      title: 'Sistema Fiscal',
      description: 'Emissão fiscal completa integrada ao PDV. Padrão brasileiro NFC-e e NF-e.',
      price: 'R$ 100,00',
      color: 'from-blue-500 to-indigo-600',
      features: ['NFC-e automática', 'NF-e completa', 'Relatórios fiscais', 'Conformidade SEFAZ']
    },
    {
      id: 'gestao',
      icon: BarChart3,
      title: 'Sistema de Gestão Completo',
      description: 'Estoque, vendas, financeiro, contas a pagar/receber, KDS para cozinha, ficha técnica e importação de notas por foto com IA.',
      price: 'R$ 100,00',
      color: 'from-purple-500 to-violet-600',
      highlight: true,
      features: ['Controle de estoque inteligente', 'KDS - Painel da Cozinha incluso', 'Ficha Técnica de Receitas inclusa', 'Importação de NF-e por FOTO com IA']
    },
    {
      id: 'webtef',
      icon: CreditCard,
      title: 'WebTEF Integrado',
      description: 'Pagamento direto na maquininha sem selecionar tipo. Menor taxa do mercado.',
      price: 'R$ 100,00',
      additionalInfo: '+ R$ 50,00 por PDV adicional',
      color: 'from-orange-500 to-red-500',
      features: ['Crédito, débito e Pix', 'Integração automática', 'Menor taxa do mercado', 'Sem seleção manual']
    },
    {
      id: 'impressao',
      icon: Printer,
      title: 'Impressão Inteligente',
      description: 'Impressão automática em impressoras térmicas com integração total.',
      price: 'Gratuito',
      color: 'from-gray-500 to-gray-700',
      free: true,
      features: ['Impressoras térmicas', 'Cupom fiscal e pedidos', 'Múltiplas impressoras', 'Impressão automática na cozinha']
    },
    {
      id: 'etiquetas',
      icon: Tag,
      title: 'Etiquetas para Cozinha',
      description: 'Com 1 clique, em 1 segundo, etiqueta pronta. Ideal para restaurantes e delivery.',
      price: 'R$ 300,00',
      color: 'from-pink-500 to-rose-600',
      features: ['Impressão instantânea', 'Ideal para cozinha', 'Organização de pedidos', 'Reduz erros']
    },
    {
      id: 'totem',
      icon: Monitor,
      title: 'Totem de Autoatendimento',
      description: 'Cliente faz o pedido sozinho. Reduz filas, aumenta vendas e diminui custos.',
      price: 'R$ 200,00/totem',
      color: 'from-cyan-500 to-blue-600',
      isNew: true,
      features: ['Interface touch', 'Integração total', 'Pedido vai direto para cozinha', 'Funciona 24h']
    },
    {
      id: 'delivery',
      icon: Truck,
      title: 'Delivery Online',
      description: 'Cardápio público com link próprio, carrinho, pagamento PIX/cartão (Mercado Pago), confirmação no WhatsApp e integração total com KDS, Fiscal e Vendas.',
      price: 'R$ 150,00/mês',
      color: 'from-rose-500 to-pink-600',
      isNew: true,
      highlight: true,
      features: [
        'Cardápio público personalizado (sualoja.com/d/slug)',
        'Pagamento PIX, crédito, débito ou dinheiro na entrega',
        'Mercado Pago integrado (PIX em 1 clique)',
        'Confirmação automática via WhatsApp',
        'Pedidos vão direto pro KDS + impressão',
        'NFC-e emitida automaticamente',
        'Toggle "abrir/fechar loja" no admin',
        'Tudo aparece em Vendas, Fiscal e Fluxo de Caixa'
      ]
    },
    {
      id: 'table_menu',
      icon: QrCode,
      title: 'Cardápio de Mesa (QR)',
      description: 'Cliente escaneia o QR Code na mesa, pede pelo celular e o pedido cai direto no PDV e na cozinha.',
      price: 'R$ 50,00',
      color: 'from-amber-500 to-orange-600',
      isNew: true,
      features: ['QR Code por mesa', 'Mesa abre automaticamente no PDV', 'Chamada de garçom com alerta sonoro', 'Contas individuais por pessoa']
    },
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white overflow-x-hidden">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-slate-900/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center gap-2">
              <img src="/js-solution-logo.png" alt="JsSolution" className="h-10 w-auto" />
              <span className="text-xl font-bold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                JsSolution
              </span>
            </div>
            
            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="#funcionalidades" className="text-gray-300 hover:text-white transition-colors">Funcionalidades</a>
              <a href="#precos" className="text-gray-300 hover:text-white transition-colors">Preços</a>
              <a href="#diferenciais" className="text-gray-300 hover:text-white transition-colors">Diferenciais</a>
            </nav>

            <div className="hidden md:flex items-center gap-4">
              <button 
                onClick={() => navigate('/admin/login')}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
              >
                Entrar
              </button>
              <button 
                onClick={() => navigate('/register')}
                className="px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full font-semibold hover:shadow-lg hover:shadow-emerald-500/25 transition-all"
              >
                Começar Agora
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-slate-900/98 backdrop-blur-lg border-t border-slate-800"
          >
            <div className="px-4 py-4 space-y-4">
              <a href="#funcionalidades" className="block text-gray-300 hover:text-white py-2">Funcionalidades</a>
              <a href="#precos" className="block text-gray-300 hover:text-white py-2">Preços</a>
              <a href="#diferenciais" className="block text-gray-300 hover:text-white py-2">Diferenciais</a>
              <div className="pt-4 border-t border-slate-800 space-y-3">
                <button 
                  onClick={() => navigate('/admin/login')}
                  className="w-full py-2.5 border border-gray-600 rounded-full"
                >
                  Entrar
                </button>
                <button 
                  onClick={() => navigate('/register')}
                  className="w-full py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full font-semibold"
                >
                  Começar Agora
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm mb-6"
            >
              <Sparkles className="w-4 h-4" />
              <span>Sistema completo para seu negócio</span>
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight mb-6"
            >
              O sistema mais completo para{' '}
              <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
                vender, automatizar e escalar
              </span>{' '}
              seu negócio
            </motion.h1>

            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg sm:text-xl text-gray-400 mb-8 max-w-3xl mx-auto"
            >
              PDV, Fiscal, Gestão, KDS para Cozinha, Ficha Técnica, Importação de Notas por Foto com IA, 
              WebTEF e Totem de Autoatendimento em um único sistema.
              <span className="text-emerald-400 font-medium"> Simplifique sua operação.</span>
            </motion.p>

            {/* Destaques das funcionalidades */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="flex flex-wrap justify-center gap-3 mb-10"
            >
              <span className="px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-emerald-300 text-sm flex items-center gap-1">
                <Camera className="w-4 h-4" /> Importa NF-e por Foto
              </span>
              <span className="px-3 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-full text-amber-300 text-sm flex items-center gap-1">
                <Monitor className="w-4 h-4" /> KDS Cozinha
              </span>
              <span className="px-3 py-1.5 bg-teal-500/20 border border-teal-500/30 rounded-full text-teal-300 text-sm flex items-center gap-1">
                <FileText className="w-4 h-4" /> Ficha Técnica
              </span>
              <span className="px-3 py-1.5 bg-blue-500/20 border border-blue-500/30 rounded-full text-blue-300 text-sm flex items-center gap-1">
                <Printer className="w-4 h-4" /> Impressão Automática
              </span>
              <span className="px-3 py-1.5 bg-purple-500/20 border border-purple-500/30 rounded-full text-purple-300 text-sm flex items-center gap-1">
                <ShoppingCart className="w-4 h-4" /> PDV Completo
              </span>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <button 
                onClick={() => navigate('/register')}
                className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full font-semibold text-lg hover:shadow-xl hover:shadow-emerald-500/25 transition-all flex items-center justify-center gap-2 group"
              >
                Começar Agora
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button 
                onClick={() => navigate('/admin/login')}
                className="w-full sm:w-auto px-8 py-4 border border-gray-600 rounded-full font-semibold text-lg hover:bg-white/5 transition-all"
              >
                Entrar / Login
              </button>
            </motion.div>

            {/* Trust Badges */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="mt-12 flex flex-wrap items-center justify-center gap-6 text-gray-500 text-sm"
            >
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span>Sem fidelidade</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span>Suporte 24/7</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span>Setup em 5 minutos</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Como Funciona */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Como o Sistema Funciona</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Simples, rápido e eficiente. Em poucos passos seu negócio estará funcionando.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Cadastre-se', desc: 'Crie sua conta em segundos', icon: Users },
              { step: '02', title: 'Configure', desc: 'Personalize para seu negócio', icon: Zap },
              { step: '03', title: 'Conecte', desc: 'Integre impressoras e maquininhas', icon: Shield },
              { step: '04', title: 'Venda', desc: 'Comece a faturar imediatamente', icon: TrendingUp },
            ].map((item, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="relative"
              >
                <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50 rounded-2xl p-6 hover:border-emerald-500/30 transition-all group">
                  <span className="text-5xl font-bold text-emerald-500/20 absolute top-4 right-4">{item.step}</span>
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <item.icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-400">{item.desc}</p>
                </div>
                {idx < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 text-emerald-500/30">
                    <ChevronRight className="w-8 h-8" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Funcionalidades */}
      <section id="funcionalidades" className="py-20 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Funcionalidades</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Todos os módulos que seu negócio precisa em um só lugar
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module, idx) => (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 border rounded-2xl p-6 overflow-hidden group hover:border-emerald-500/50 transition-all ${
                  module.highlight ? 'border-emerald-500/30 ring-1 ring-emerald-500/20' : 'border-slate-700/50'
                }`}
              >
                {module.isNew && (
                  <div className="absolute top-4 right-4 px-3 py-1 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full text-xs font-semibold">
                    NOVO
                  </div>
                )}
                {module.free && (
                  <div className="absolute top-4 right-4 px-3 py-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full text-xs font-semibold">
                    GRÁTIS
                  </div>
                )}
                
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${module.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <module.icon className="w-7 h-7 text-white" />
                </div>

                <h3 className="text-xl font-bold mb-2">{module.title}</h3>
                <p className="text-gray-400 text-sm mb-4">{module.description}</p>

                <div className="space-y-2 mb-4">
                  {module.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-300">
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t border-slate-700/50">
                  <p className="text-2xl font-bold text-emerald-400">{module.price}</p>
                  {module.additionalInfo && (
                    <p className="text-xs text-gray-500 mt-1">{module.additionalInfo}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Diferencial: Importação de Nota */}
      <section id="diferenciais" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full text-purple-400 text-sm mb-6">
                <Camera className="w-4 h-4" />
                <span>Exclusivo</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Importação Automática de{' '}
                <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Notas Fiscais
                </span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Com apenas <span className="text-white font-semibold">1 foto</span> da nota fiscal, o sistema:
              </p>
              <div className="space-y-4">
                {[
                  { icon: Camera, text: 'Lê automaticamente todos os dados' },
                  { icon: FileText, text: 'Lança produtos no estoque' },
                  { icon: DollarSign, text: 'Registra contas a pagar' },
                  { icon: BarChart3, text: 'Organiza valores e impostos' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4 bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-purple-400" />
                    </div>
                    <span className="text-gray-300">{item.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-3xl flex items-center justify-center border border-purple-500/20">
                <div className="text-center">
                  <Camera className="w-24 h-24 text-purple-400 mx-auto mb-4" />
                  <p className="text-gray-400">Tire uma foto da nota</p>
                  <p className="text-purple-400 font-semibold mt-2">→ Dados importados automaticamente</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Totem Section */}
      <section className="py-20 bg-gradient-to-b from-slate-900/50 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="aspect-[4/5] bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-3xl flex items-center justify-center border border-cyan-500/20">
                <div className="text-center">
                  <Monitor className="w-32 h-32 text-cyan-400 mx-auto mb-4" />
                  <p className="text-2xl font-bold text-white">Totem Touch</p>
                  <p className="text-cyan-400">Autoatendimento</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-sm mb-6">
                <Sparkles className="w-4 h-4" />
                <span>Novo Módulo</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Totem de{' '}
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  Autoatendimento
                </span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Cliente faz o pedido sozinho. Reduz filas, aumenta vendas e diminui custos operacionais.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {[
                  'Interface touch intuitiva',
                  'Integração total com PDV',
                  'Pedido direto para cozinha',
                  'WebTEF e Pix integrados',
                  'Funciona 24h',
                  'Aumenta ticket médio',
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-cyan-500" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                {['Restaurantes', 'Fast-food', 'Lanchonetes', 'Padarias'].map((item, idx) => (
                  <span key={idx} className="px-4 py-2 bg-slate-800 rounded-full text-sm text-gray-300 border border-slate-700">
                    <Utensils className="w-4 h-4 inline mr-2" />
                    {item}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Preços Interativos */}
      <section id="precos" className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Monte Seu Plano</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Selecione os módulos que seu negócio precisa e veja o valor em tempo real
            </p>
          </div>

          <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50 rounded-3xl p-6 md:p-8">
            <div className="space-y-4">
              {modules.map((module) => (
                <div 
                  key={module.id}
                  className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl border transition-all ${
                    selectedModules[module.id]?.selected 
                      ? 'bg-emerald-500/10 border-emerald-500/30' 
                      : 'bg-slate-800/30 border-slate-700/30 opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-4 mb-4 sm:mb-0">
                    <button
                      onClick={() => toggleModule(module.id)}
                      className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                        selectedModules[module.id]?.selected 
                          ? 'bg-emerald-500 border-emerald-500' 
                          : 'border-gray-600'
                      }`}
                    >
                      {selectedModules[module.id]?.selected && <Check className="w-4 h-4" />}
                    </button>
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${module.color} flex items-center justify-center`}>
                      <module.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold flex items-center gap-2">
                        {module.title}
                        {module.isNew && <span className="text-xs bg-cyan-500 px-2 py-0.5 rounded-full">NOVO</span>}
                        {module.free && <span className="text-xs bg-emerald-500 px-2 py-0.5 rounded-full">GRÁTIS</span>}
                      </h4>
                      <p className="text-sm text-gray-500">{module.price}</p>
                    </div>
                  </div>

                  {(module.id === 'pdv' || module.id === 'webtef' || module.id === 'totem') && selectedModules[module.id]?.selected && (
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-400">
                        {module.id === 'totem' ? 'Totens:' : 'PDVs:'}
                      </span>
                      <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg p-1">
                        <button 
                          onClick={() => updateQuantity(module.id, -1)}
                          className="w-8 h-8 rounded-md hover:bg-slate-600 flex items-center justify-center"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-semibold">{selectedModules[module.id]?.quantity}</span>
                        <button 
                          onClick={() => updateQuantity(module.id, 1)}
                          className="w-8 h-8 rounded-md hover:bg-slate-600 flex items-center justify-center"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Total com Promoção */}
            <div className="mt-8 pt-6 border-t border-slate-700/50">
              {/* Banner de Promoção */}
              <div className="bg-gradient-to-r from-red-600 via-pink-600 to-purple-600 rounded-xl p-4 mb-6 text-center">
                <div className="flex items-center justify-center gap-2 text-yellow-300 font-bold text-lg mb-1">
                  <Gift className="w-5 h-5" />
                  PROMOÇÃO DE LANÇAMENTO
                  <Gift className="w-5 h-5" />
                </div>
                <p className="text-3xl font-black text-white">50% DE DESCONTO</p>
                <p className="text-sm text-yellow-200">+ 3 dias grátis para testar!</p>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="text-center sm:text-left">
                  <p className="text-gray-400 text-sm line-through">De R$ {calculateTotal().toFixed(2).replace('.', ',')}/mês</p>
                  <p className="text-4xl font-bold text-emerald-400">
                    R$ {(calculateTotal() * 0.5).toFixed(2).replace('.', ',')}
                    <span className="text-lg text-gray-500 font-normal">/mês</span>
                  </p>
                </div>
                <div className="w-full sm:w-auto text-center flex flex-col sm:flex-row gap-3">
                  <button 
                    onClick={() => navigate('/register')}
                    className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full font-semibold text-lg hover:shadow-xl hover:shadow-emerald-500/25 transition-all flex items-center justify-center gap-2"
                  >
                    <Clock className="w-5 h-5" />
                    Testar 3 Dias Grátis
                  </button>
                  <button 
                    onClick={() => {
                      localStorage.setItem('selectedModules', JSON.stringify(selectedModules));
                      navigate('/comprar');
                    }}
                    className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full font-semibold text-lg hover:shadow-xl hover:shadow-blue-500/25 transition-all flex items-center justify-center gap-2"
                  >
                    <CreditCard className="w-5 h-5" />
                    Comprar Agora
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500 text-center mt-3">Sem compromisso • Cancele quando quiser</p>
            </div>
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="py-20 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Por que escolher o JsSolution?</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Zap, title: 'Automação Total', desc: 'Menos trabalho manual, mais eficiência' },
              { icon: Camera, title: 'Leitura de NF por Foto', desc: 'Exclusivo no mercado' },
              { icon: CreditCard, title: 'Menor Taxa de Pagamento', desc: 'Economize em cada venda' },
              { icon: Clock, title: 'Venda Sem Fila', desc: 'Totem de autoatendimento' },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50 rounded-2xl p-6 text-center hover:border-emerald-500/30 transition-all"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-emerald-400" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-3xl p-8 md:p-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Pronto para transformar seu negócio?
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Comece agora e veja resultados em minutos. Sem complicação, sem contratos longos.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button 
                onClick={() => navigate('/register')}
                className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full font-semibold text-lg hover:shadow-xl hover:shadow-emerald-500/25 transition-all"
              >
                Começar Gratuitamente
              </button>
              <button 
                onClick={() => navigate('/admin/login')}
                className="w-full sm:w-auto px-8 py-4 border border-gray-600 rounded-full font-semibold text-lg hover:bg-white/5 transition-all"
              >
                Já tenho conta
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img src="/js-solution-logo.png" alt="JsSolution" className="h-8 w-auto" />
              <span className="font-bold text-lg">JsSolution</span>
            </div>
            
            <div className="flex items-center gap-6 text-sm">
              <a href="/contato" className="text-gray-400 hover:text-emerald-400 transition-colors">
                Contato
              </a>
              <a href="/suporte" className="text-gray-400 hover:text-emerald-400 transition-colors">
                Suporte
              </a>
              <a href="https://wa.me/5517996462254" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </a>
            </div>
            
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} JsSolution. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
